/**
 * Core editor configuration constants for the FlyerCoordinateEditor.
 *
 * @property DEFAULT_BOX_WIDTH - Initial width in pixels for new coordinate boxes
 * @property DEFAULT_BOX_HEIGHT - Initial height in pixels for new coordinate boxes
 * @property ZOOM_MIN - Minimum allowed zoom level (0.5 = 50%)
 * @property ZOOM_MAX - Maximum allowed zoom level (5 = 500%)
 * @property ZOOM_STEP - Zoom multiplier for button-based zoom in/out
 * @property WHEEL_ZOOM_STEP - Zoom multiplier for mouse wheel zoom
 * @property DEFAULT_CONTAINER_WIDTH - Default canvas container width in pixels
 * @property DEFAULT_CONTAINER_HEIGHT - Default canvas container height in pixels
 * @property TEMP_ITEM_PREFIX - Prefix for temporary item IDs
 * @property AI_EXTRACT_TEMP_PREFIX - Prefix for AI extraction temporary item IDs
 */
export const EDITOR_CONSTANTS = {
  DEFAULT_BOX_WIDTH: 300,
  DEFAULT_BOX_HEIGHT: 300,
  ZOOM_MIN: 0.5,
  ZOOM_MAX: 5,
  ZOOM_STEP: 1.2,
  WHEEL_ZOOM_STEP: 1.1,
  DEFAULT_CONTAINER_WIDTH: 800,
  DEFAULT_CONTAINER_HEIGHT: 600,
  TEMP_ITEM_PREFIX: "temp_",
  AI_EXTRACT_TEMP_PREFIX: "ai-extract-temp-",
};

/**
 * Color palette for the FlyerCoordinateEditor and related components.
 *
 * Centralizes all color values for consistent theming across the viewer UI.
 * Includes primary brand colors, state colors, grays, shadows, and transparency variants.
 *
 * @property PRIMARY_TEAL - Main brand teal color for selected states
 * @property PRIMARY_ORANGE - Main brand orange color for primary actions
 * @property PRIMARY_ORANGE_HOVER - Darker orange for hover states
 * @property AI_EXTRACT_PURPLE - Purple/pink color for AI extraction boxes
 * @property AI_EXTRACT_PURPLE_HOVER - Darker purple/pink for AI extraction button hover
 * @property SUCCESS_GREEN - Green for success states and confirmations
 * @property ERROR_RED - Red for error states and deletions
 * @property LIGHT_GRAY_BORDER - Light gray for subtle borders
 * @property MEDIUM_GRAY_BORDER - Medium gray for default borders
 * @property TEXT_DARK - Dark text color for primary content
 * @property BACKGROUND_LIGHT - Light background color
 * @property WHITE - Pure white
 * @property INFO_PILL_BG - Background for informational pills
 * @property INFO_PILL_TEXT - Text color for informational pills
 * @property GRAY_300 - Standard gray for dividers
 * @property SHADOW_LIGHT_1 - Light shadow for subtle elevation
 * @property SHADOW_LIGHT_2 - Lighter shadow for minimal elevation
 * @property SHADOW_SOFT - Soft shadow for cards
 * @property ACCENT_TEAL_TRANSPARENT - Semi-transparent teal for highlights
 * @property LIST_ITEM_BG - Very light background for list items
 * @property SCROLL_TRACK - Scrollbar track color
 * @property SCROLL_THUMB - Scrollbar thumb color
 * @property SCROLL_THUMB_HOVER - Scrollbar thumb hover color
 */
export const VIEWER_COLORS = {
  PRIMARY_TEAL: "#03b7cf",
  PRIMARY_ORANGE: "#ff8038",
  PRIMARY_ORANGE_HOVER: "#e67332",
  AI_EXTRACT_PURPLE: "#FF2D95",
  AI_EXTRACT_PURPLE_HOVER: "#E01A7D",
  SUCCESS_GREEN: "#4caf50",
  ERROR_RED: "#d32f2f",
  LIGHT_GRAY_BORDER: "#e0e0e0",
  MEDIUM_GRAY_BORDER: "#cccccc",
  TEXT_DARK: "#1a1a1a",
  BACKGROUND_LIGHT: "#f5f5f5",
  WHITE: "#ffffff",
  INFO_PILL_BG: "#f0f9ff",
  INFO_PILL_TEXT: "#0284c7",
  GRAY_300: "#d1d5db",
  SHADOW_LIGHT_1: "rgba(0, 0, 0, 0.08)",
  SHADOW_LIGHT_2: "rgba(0, 0, 0, 0.06)",
  SHADOW_SOFT: "rgba(0, 0, 0, 0.1)",
  ACCENT_TEAL_TRANSPARENT: "rgba(3, 183, 207, 0.15)",
  LIST_ITEM_BG: "rgba(0,0,0,0.02)",
  SCROLL_TRACK: "#f1f1f1",
  SCROLL_THUMB: "#888",
  SCROLL_THUMB_HOVER: "#555",
};

/**
 * Configuration constants for coordinate box rendering and interactions.
 *
 * Defines dimensions, styling, and layout parameters for the coordinate boxes,
 * connector lines, information cards, text rendering, icons, and display preferences.
 * All measurements are in pixels unless otherwise specified.
 *
 * @property MIN_SIZE - Minimum width/height in pixels for coordinate boxes
 * @property BOX - Stroke widths for coordinate box rectangle
 * @property CONNECTOR - Line and dot styling for box-to-card connectors
 * @property CARD - Dimensions and styling for information cards attached to boxes
 * @property TEXT - Font sizing and styling for text within cards
 * @property EDIT_ICON - Hover effects and styling for edit icons
 * @property PENCIL_ICON - SVG path points and stroke widths for pencil icon
 * @property DISPLAY_NAME_FALLBACK - Default text when item name is missing
 */
export const COORDINATE_BOX_CONSTANTS = {
  MIN_SIZE: 10,
  BOX: {
    STROKE_WIDTH_SELECTED: 3,
    STROKE_WIDTH_DEFAULT: 2,
  },
  CONNECTOR: {
    STROKE_WIDTH: 2,
    DOT_RADIUS: 4,
    END_Y_OFFSET: 10,
  },
  CARD: {
    WIDTH: 100,
    OFFSET_X: 8,
    SHADOW_OFFSET_PRIMARY: 2,
    SHADOW_OFFSET_SECONDARY: 1,
    SHADOW_CORNER_RADIUS: 8,
    BORDER_CORNER_RADIUS: 4,
    BORDER_WIDTH_SELECTED: 2.5,
    BORDER_WIDTH_DEFAULT: 1.5,
    PADDING: 6,
    TITLE_PADDING_X: 6,
    TITLE_PADDING_Y: 6,
    TITLE_MIN_HEIGHT: 10,
    TITLE_LINE_HEIGHT_PX: 10,
    DETAILS_LINE_HEIGHT_PX: 9,
    DETAILS_SECTION_GAP: 4,
    DETAILS_PILL_PADDING_X: 4,
    DETAILS_PILL_TOP_OFFSET: 2,
    DETAILS_PILL_HEIGHT_EXTRA: 5,
    DETAILS_TEXT_PADDING_X: 7,
    DETAILS_TEXT_PADDING_Y: 4.5,
    DETAILS_PILL_CORNER_RADIUS: 3,
    EDIT_ICON_PADDING: 4,
    EDIT_ICON_SIZE: 16,
    CONTENT_WIDTH_SUBTRACT: 12,
    DETAILS_CONTENT_WIDTH_SUBTRACT: 18,
    DETAILS_PILL_WIDTH_SUBTRACT: 8,
    DETAILS_TEXT_WIDTH_SUBTRACT: 14,
  },
  TEXT: {
    AVG_CHAR_WIDTH: 5,
    TITLE_FONT_SIZE: 7,
    TITLE_FONT_STYLE: "600",
    TITLE_LINE_HEIGHT: 1.3,
    DETAILS_FONT_SIZE: 6,
    DETAILS_FONT_STYLE: "600",
    DETAILS_LINE_HEIGHT: 1.2,
  },
  EDIT_ICON: {
    HOVER_RING_PADDING: 1.5,
    STROKE_WIDTH_HOVER: 1.5,
    STROKE_WIDTH_DEFAULT: 1,
    SHADOW_BLUR: 3,
    SHADOW_OFFSET_Y: 1.5,
  },
  PENCIL_ICON: {
    BODY_POINTS: [-2.5, 2.5, 1, -1],
    TIP_POINTS: [1, -1, 2.5, -2.5],
    DETAIL_POINTS: [-3, 3, -1.5, 1.5],
    STROKE_WIDTH_MAIN: 1.3,
    STROKE_WIDTH_DETAIL: 1,
  },
  DISPLAY_NAME_FALLBACK: "Unknown Item",
};

/**
 * Configuration constants for image loading and scaling behavior.
 *
 * @property PADDING_RATIO - Scale ratio to add padding around images (0.6 = 60% padding)
 * @property LOG_SRC_TRIM_SHORT - Character limit for short image URL logs
 * @property LOG_SRC_TRIM_LONG - Character limit for long image URL logs
 */
export const IMAGE_LOADER_CONSTANTS = {
  PADDING_RATIO: 0.6,
  LOG_SRC_TRIM_SHORT: 50,
  LOG_SRC_TRIM_LONG: 100,
};

/**
 * Configuration constants for the thumbnail bar component.
 *
 * @property SKELETON_COUNT - Number of skeleton placeholders to show while loading
 * @property SKELETON_WIDTH - Width of skeleton placeholders in pixels
 * @property SKELETON_HEIGHT - Height of skeleton placeholders in pixels
 * @property SKELETON_BORDER_RADIUS - Border radius for skeleton placeholders
 */
export const THUMBNAIL_BAR_CONSTANTS = {
  SKELETON_COUNT: 3,
  SKELETON_WIDTH: 120,
  SKELETON_HEIGHT: 80,
  SKELETON_BORDER_RADIUS: "4px",
};

/**
 * Configuration constants for modal dialogs.
 *
 * @property Z_INDEX - Z-index for modal overlay to appear above other content
 * @property PAPER_HEIGHT - Modal paper height as viewport percentage
 * @property PAPER_MAX_HEIGHT - Maximum modal paper height in pixels
 * @property CHIP_HEIGHT - Height of status chips in pixels
 * @property CHIP_FONT_SIZE - Font size for chip text
 */
export const MODAL_CONSTANTS = {
  Z_INDEX: 10001,
  PAPER_HEIGHT: "80vh",
  PAPER_MAX_HEIGHT: 700,
  CHIP_HEIGHT: 20,
  CHIP_FONT_SIZE: "0.7rem",
};

/**
 * Configuration constants for the save confirmation modal component.
 *
 * Defines spacing, sizing, and layout values for the modal that displays
 * coordinate changes before saving.
 *
 * @property LIST_ITEM_BORDER_WIDTH - Border width for change list items in pixels
 * @property LIST_ITEM_BORDER_RADIUS - Border radius for list items
 * @property LIST_ITEM_MARGIN_BOTTOM - Bottom margin for list items
 * @property ICON_MARGIN_RIGHT - Right margin for icons
 * @property TITLE_GAP - Gap between title elements
 * @property CHIP_GAP - Gap between chips
 * @property CHIP_MARGIN_TOP - Top margin for chips
 * @property DESCRIPTION_MARGIN_BOTTOM - Bottom margin for descriptions
 * @property TITLE_ICON_GAP - Gap between title and icon
 * @property SECTION_MARGIN_BOTTOM - Bottom margin for sections
 * @property SECTION_DIVIDER_MARGIN_TOP - Top margin for section dividers
 * @property SECTION_TITLE_MARGIN_BOTTOM - Bottom margin for section titles
 * @property SECTION_CAPTION_MARGIN_BOTTOM - Bottom margin for section captions
 * @property EMPTY_STATE_PADDING_Y - Vertical padding for empty state
 * @property ACTIONS_PADDING - Padding for action button area
 * @property ACTIONS_JUSTIFY - Flexbox justify value for action buttons
 */
export const SAVE_CONFIRMATION_MODAL_CONSTANTS = {
  LIST_ITEM_BORDER_WIDTH: 4,
  LIST_ITEM_BORDER_RADIUS: "4px",
  LIST_ITEM_MARGIN_BOTTOM: 1,
  ICON_MARGIN_RIGHT: 2,
  TITLE_GAP: 1,
  CHIP_GAP: 0.5,
  CHIP_MARGIN_TOP: 0.5,
  DESCRIPTION_MARGIN_BOTTOM: 2,
  TITLE_ICON_GAP: 1,
  SECTION_MARGIN_BOTTOM: 3,
  SECTION_DIVIDER_MARGIN_TOP: 2,
  SECTION_TITLE_MARGIN_BOTTOM: 1,
  SECTION_CAPTION_MARGIN_BOTTOM: 1,
  EMPTY_STATE_PADDING_Y: 4,
  ACTIONS_PADDING: 2,
  ACTIONS_JUSTIFY: "space-between",
};

/**
 * Configuration constants for the item selection dialog component.
 *
 * Defines spacing, sizing, and layout values for the dialog that allows
 * users to select flyer items to link with coordinate boxes.
 *
 * @property AVATAR_SIZE - Size of item avatar images in pixels
 * @property SEARCH_MARGIN_BOTTOM - Bottom margin for search input
 * @property TITLE_GAP - Gap between title elements
 * @property CHIP_GAP - Gap between chips
 * @property CHIP_MARGIN_TOP - Top margin for chips
 * @property RESULTS_PADDING_Y - Vertical padding for results area
 * @property SECTION_MARGIN_BOTTOM - Bottom margin for sections
 * @property SECTION_TITLE_MARGIN_BOTTOM - Bottom margin for section titles
 * @property SECTION_CAPTION_MARGIN_BOTTOM - Bottom margin for section captions
 * @property EMPTY_STATE_TEXT_MARGIN_BOTTOM - Bottom margin for empty state text
 * @property ACTIONS_PADDING - Padding for action button area
 * @property ACTIONS_JUSTIFY - Flexbox justify value for action buttons
 */
export const ITEM_SELECTION_DIALOG_CONSTANTS = {
  AVATAR_SIZE: 50,
  SEARCH_MARGIN_BOTTOM: 2,
  TITLE_GAP: 1,
  CHIP_GAP: 0.5,
  CHIP_MARGIN_TOP: 0.5,
  RESULTS_PADDING_Y: 4,
  SECTION_MARGIN_BOTTOM: 3,
  SECTION_TITLE_MARGIN_BOTTOM: 1,
  SECTION_CAPTION_MARGIN_BOTTOM: 1,
  EMPTY_STATE_TEXT_MARGIN_BOTTOM: 2,
  ACTIONS_PADDING: 2,
  ACTIONS_JUSTIFY: "space-between",
};

/**
 * Style configuration constants for the main viewer layout.
 *
 * Defines spacing, sizing, and layout values for viewer controls, navigation,
 * thumbnails, and buttons.
 *
 * @property CONTROLS_GAP - Gap between control buttons in pixels
 * @property CONTROLS_PADDING - Padding around control button groups in pixels
 * @property NAVIGATION_GAP - Gap between navigation elements in pixels (desktop)
 * @property NAVIGATION_GAP_MOBILE - Gap between navigation elements in pixels (mobile)
 * @property THUMBNAIL_GAP - Gap between thumbnail items in pixels
 * @property THUMBNAIL_PADDING_Y - Vertical padding for thumbnail bar in pixels
 * @property THUMBNAIL_SCROLLBAR_HEIGHT - Height of thumbnail scrollbar in pixels
 * @property THUMBNAIL_SCROLLBAR_RADIUS - Border radius of thumbnail scrollbar in pixels
 * @property THUMBNAIL_BORDER_WIDTH - Border width for selected thumbnail in pixels
 * @property THUMBNAIL_BORDER_RADIUS - Border radius for thumbnails in pixels
 * @property THUMBNAIL_HEIGHT - Height of thumbnail images in pixels
 * @property SAVE_BUTTON_GAP - Gap between save button elements in pixels
 */
export const VIEWER_STYLE_CONSTANTS = {
  CONTROLS_GAP: 16,
  CONTROLS_PADDING: 13,
  NAVIGATION_GAP: 16,
  NAVIGATION_GAP_MOBILE: 8,
  THUMBNAIL_GAP: 8,
  THUMBNAIL_PADDING_Y: 8,
  THUMBNAIL_SCROLLBAR_HEIGHT: 6,
  THUMBNAIL_SCROLLBAR_RADIUS: 3,
  THUMBNAIL_BORDER_WIDTH: 3,
  THUMBNAIL_BORDER_RADIUS: 4,
  THUMBNAIL_HEIGHT: 80,
  SAVE_BUTTON_GAP: 8,
};

/**
 * Configuration constants for the page navigator component.
 *
 * @property PAGE_LABEL_MIN_WIDTH - Minimum width for page label display
 */
export const PAGE_NAVIGATOR_CONSTANTS = {
  PAGE_LABEL_MIN_WIDTH: "100px",
};
